/**
 * "View as customer": opens the customer's manage page for one
 * appointment as a short-lived, read-only staff preview, in a new tab.
 *
 * The tab is opened BEFORE the request, while the click still counts as a user
 * gesture, so a pop-up blocker does not swallow it; it is pointed at the signed
 * link when the server answers, or closed if it doesn't. A 503 means this
 * deployment has no manage page: the action is hidden for the rest of the session.
 */
import { createAdminAppointmentManagePreview } from "@v0y4ge/api-client";
import { can } from "../../shell/access";

let unavailable = false;

/** Whether to offer the action (operator, 7 Oct): everyone who can open the appointment
 *  list (the same gate as the `appointments` route), on a deployment that has the page.
 *  The server checks its own capability; a refusal shows the usual "couldn't open" toast. */
export const canViewAsCustomer = () => !unavailable && can("appointments.read");

export type ManagePreviewResult = "opened" | "unavailable" | "error";

export async function openManagePreview(appointmentId: string): Promise<ManagePreviewResult> {
  const tab = typeof window !== "undefined" ? window.open("about:blank", "_blank") : null;
  try {
    const res = await createAdminAppointmentManagePreview(appointmentId);
    const url = res.data.url;
    if (tab && !tab.closed) {
      try { tab.opener = null; } catch { /* cross-browser: best effort */ }
      tab.location.href = url;
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
    }
    return "opened";
  } catch (e) {
    if (tab && !tab.closed) tab.close();
    if ((e as { status?: number })?.status === 503) { unavailable = true; return "unavailable"; }
    return "error";
  }
}
